import { useCallback, useEffect, useMemo, useState } from "react"
import { Bell, ExternalLink, Loader2, Mail, Webhook } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { Badge, Button } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { useToast } from "@/hooks"
import {
  agentHandoffNotificationResponseToSetting,
  notificationDestinationResponseToDestination,
  useGetAgentHandoffNotificationSettingApi,
  useGetNotificationDestinationsApi,
} from "@/hooks/api"
import type {
  AgentHandoffNotificationSetting,
  NotificationDestination,
} from "@/hooks/api/notifications"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface HandoffNotificationSectionProps {
  organizationId: string
  workspaceId: string
  agentId?: string
  humanHandoffEnabled?: boolean
  canManageHandoffNotifications: boolean
}

const EMPTY_SETTING: AgentHandoffNotificationSetting = {
  mode: "disabled",
  destinationIds: [],
  effectiveDestinationIds: [],
  workspaceDefaultDestinationIds: [],
  workspaceDefaultAvailable: false,
  allowedDestinationTypes: [],
}

export const HandoffNotificationSection = ({
  organizationId,
  workspaceId,
  agentId,
  humanHandoffEnabled,
  canManageHandoffNotifications,
}: HandoffNotificationSectionProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [destinations, setDestinations] = useState<NotificationDestination[]>(
    []
  )
  const [setting, setSetting] =
    useState<AgentHandoffNotificationSetting>(EMPTY_SETTING)
  const [isLoading, setIsLoading] = useState(false)

  const { fetchFn: fetchDestinations } = useGetNotificationDestinationsApi(
    organizationId,
    workspaceId
  )
  const { fetchFn: fetchSetting } = useGetAgentHandoffNotificationSettingApi(
    organizationId,
    workspaceId,
    agentId || ""
  )

  const showLoadError = useCallback(
    (error: unknown) => {
      alertToast(
        getUserFacingApiErrorToastProps(error, t, {
          title: t(
            "workspace.agent.basicForm.humanHandoff.notifications.loadFailed"
          ),
          fallbackDescription: t(
            "workspace.agent.basicForm.humanHandoff.notifications.loadFailedDescription"
          ),
        })
      )
    },
    [alertToast, t]
  )

  useEffect(() => {
    if (!organizationId || !workspaceId || !agentId) return
    let active = true
    setIsLoading(true)
    Promise.all([fetchDestinations(), fetchSetting()])
      .then(([destinationResponse, settingResponse]) => {
        if (!active) return
        setDestinations(
          (destinationResponse?.data || []).map(
            notificationDestinationResponseToDestination
          )
        )
        if (settingResponse?.data) {
          setSetting(
            agentHandoffNotificationResponseToSetting(settingResponse.data)
          )
        }
      })
      .catch((error) => {
        if (active) showLoadError(error)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [
    agentId,
    fetchDestinations,
    fetchSetting,
    organizationId,
    showLoadError,
    workspaceId,
  ])

  const destinationMap = useMemo(
    () =>
      new Map(destinations.map((destination) => [destination.id, destination])),
    [destinations]
  )
  const effectiveDestinations = setting.effectiveDestinationIds.flatMap(
    (id) => {
      const destination = destinationMap.get(id)
      return destination ? [destination] : []
    }
  )
  const modeLabel = t(
    `workspace.agent.basicForm.humanHandoff.notifications.summaryModes.${setting.mode}`
  )

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <h4 className="font-medium">
              {t("workspace.agent.basicForm.humanHandoff.notifications.title")}
            </h4>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              "workspace.agent.basicForm.humanHandoff.notifications.summaryDescription"
            )}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link
            to={`/workspaces/${workspaceId}?tab=notifications&event=agent_handoff.triggered&agent=${agentId || ""}`}
          >
            {t(
              canManageHandoffNotifications
                ? "workspace.notifications.manageWorkspace"
                : "workspace.notifications.viewWorkspace"
            )}
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>

      {!humanHandoffEnabled ? (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {t(
            "workspace.agent.basicForm.humanHandoff.notifications.disabledUntilHandoffEnabled"
          )}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("common.processing")}
        </div>
      ) : (
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              {t(
                "workspace.agent.basicForm.humanHandoff.notifications.behaviorLabel"
              )}
            </dt>
            <dd className="mt-1.5">
              <Badge
                variant="outline"
                className={cn(
                  "font-medium",
                  setting.mode === "disabled" &&
                    "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300",
                  setting.mode === "custom" &&
                    "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300"
                )}
              >
                {modeLabel}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              {t(
                "workspace.agent.basicForm.humanHandoff.notifications.effectiveDestinationsLabel"
              )}
            </dt>
            <dd className="mt-1.5">
              {setting.mode === "disabled" ? (
                <span className="text-sm text-muted-foreground">
                  {t(
                    "workspace.agent.basicForm.humanHandoff.notifications.disabledSummary"
                  )}
                </span>
              ) : effectiveDestinations.length > 0 ? (
                <div className="space-y-1.5">
                  {effectiveDestinations.map((destination) => {
                    const DestinationIcon =
                      destination.destinationType === "email" ? Mail : Webhook
                    return (
                      <div
                        key={destination.id}
                        className="flex min-w-0 items-start gap-2"
                      >
                        <DestinationIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 break-words text-sm">
                          {destination.name}
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {t(
                    "workspace.agent.basicForm.humanHandoff.notifications.noneEffectiveDescription"
                  )}
                </span>
              )}
            </dd>
          </div>
        </dl>
      )}
    </section>
  )
}
